import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { AdditionalModule, ImplementationPackage, SubscriptionData, SubscriptionPackage } from '../BillingTypes';
import { calculateNewSubscriptionAmount, formatPriceOnUserLocale } from '../functions/billingUtils';

const SubscriptionPriceSubtotal: React.FC<{
    packages: SubscriptionPackage[],
    additionalModules: AdditionalModule[];
    implementationPackages?: ImplementationPackage[];
    updatedSubscription: SubscriptionData
}> = ({ packages, additionalModules, implementationPackages, updatedSubscription }) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();

    // Get the chosen implementation package object
    const chosenImplementationPackage = (implementationPackages || []).find(
        implementation => implementation.id === updatedSubscription.implementation_package
    );

    // Format the price based on the user locale
    const formatPrice = (amount: number) => {
        return new Intl.NumberFormat(userLocale, {
            style: "decimal",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(Math.round(amount));
    };

    // Calculate the savings when paying per year
    const calculateYearlySavings = () => {

        // Calculate the yearly price for the monthly subscription
        const yearlyPriceForMonthlySubscription = Number(calculateNewSubscriptionAmount({
            packages,
            additionalModules,
            updatedSubscription: updatedSubscription, 
            displayPrice: 'yearly',
            returnAmount: 'totalExclVat',
            userLocale,
            subscriptionDuration: 'monthly',
            returnAsNumber: true
        }));

        // Calculate the yearly price for the yearly subscription
        const yearlyPriceForYearlySubscription = Number(calculateNewSubscriptionAmount({
            packages,
            additionalModules,
            updatedSubscription: updatedSubscription, 
            displayPrice: 'yearly',
            returnAmount: 'totalExclVat',
            userLocale,
            subscriptionDuration: 'yearly',
            returnAsNumber: true
        }));

        // Calculate the yearly savings
        if (!isNaN(yearlyPriceForMonthlySubscription) && !isNaN(yearlyPriceForYearlySubscription)) {
            const yearlySavings = yearlyPriceForMonthlySubscription - yearlyPriceForYearlySubscription;

            return formatPrice(yearlySavings);
        }

        return formatPrice(0);
    };

    return (
        <div className="price-summary">
            {updatedSubscription.duration === "monthly" ? (
                <div className="monthly-summary">
                    <span className="savings-info">
                        {t('billing.general.yearly_save_label', { 
                            price: calculateYearlySavings()
                        })}
                    </span>
                    <span className='savings-info-divider'>•</span>
                    <span className="total-price">
                        {t('billing.general.total_price_month', { 
                            price: calculateNewSubscriptionAmount({
                                packages,
                                additionalModules,
                                updatedSubscription: updatedSubscription, 
                                displayPrice: 'monthly',
                                returnAmount: 'totalExclVat',
                                userLocale
                            })
                        })}
                    </span>
                </div>
            ) : (
                <div className="yearly-summary">
                    <span className="total-price">
                        <span>
                            {t('billing.general.total_price_year', { 
                                price: calculateNewSubscriptionAmount({
                                    packages,
                                    additionalModules,
                                    updatedSubscription: updatedSubscription, 
                                    displayPrice: 'yearly',
                                    returnAmount: 'totalExclVat',
                                    userLocale
                                })     
                            })}
                        </span> 
                        <span> </span>
                        <span className="total-price"> 
                            {t('billing.general.total_price_year_per_month', {
                                price: calculateNewSubscriptionAmount({
                                    packages,
                                    additionalModules,
                                    updatedSubscription: updatedSubscription, 
                                    displayPrice: 'monthly',
                                    returnAmount: 'totalExclVat',
                                    userLocale
                                })
                            })}
                        </span>
                    </span>
                </div>
            )}
            {chosenImplementationPackage && (
                <span className="one-time-info">
                    {t('billing.general.plus_one_time_label', {
                        price: formatPriceOnUserLocale(parseFloat(chosenImplementationPackage.price), userLocale)
                    })}
                </span>
            )}
        </div>
    );
};

export default SubscriptionPriceSubtotal;